import * as classNames from 'classnames';
import { $getRoot } from 'lexical';
import _ from 'lodash';
import { confirmLexicalStateInTextComponent } from 'modules/draftjs2lexical';
import { createLexicalTextEditor } from 'modules/Lexical/editors';
import React, { useMemo } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector, useDispatch, batch } from 'react-redux';
import AssetActions from 'components/AssetActions';
import { useScrollIntoView } from 'components/AssetList/hooks';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import { ModalType } from 'const';
import { addSelectedAssetPanelComponent, removeSelectedAssetPanelComponent } from 'containers/App/actions';
import { selectedComponentItem } from 'containers/App/selectors';
import { deleteComponent } from 'containers/Documents/actions';
import { showModal } from 'containers/ModalManager/actions';
import { setAssetIdToScroll, setSelectedTextComponent } from 'containers/ProjectPanel/actions';
import * as ProjectPanelModels from 'containers/ProjectPanel/models';
import { assetIdToScroll as assetIdToScrollSelector, selectComponentCount } from 'containers/ProjectPanel/selectors';
import { Priority, useClickOutside } from 'hooks/useClickOutside';
import { TextComponentMap } from 'models';
import { trimText } from 'utils/html';
import styles from './styles.module.scss';
import { useTextComponentItemDragSource } from './useTextComponentItemDragSource';

type Props = {
  allowDelete: boolean;
  component: TextComponentMap;
  dataTestId?: string;
};

const TextComponentItem: React.FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const { allowDelete, component, dataTestId = 'textComponentItem' } = props;
  const componentId = component.get('id');
  const assetIdToScroll = useSelector(assetIdToScrollSelector);
  const isSelected = useSelector(selectedComponentItem(componentId));
  const textCount = useSelector(selectComponentCount(componentId));
  const { dragRef, preview, isDragging } = useTextComponentItemDragSource({ component });

  const setAssetIdToScrollHandler: ProjectPanelModels.ActionCreator.SetAssetIdToScroll = (...args) => {
    return dispatch(setAssetIdToScroll(...args));
  };

  const { elementRef } = useScrollIntoView(componentId, assetIdToScroll, setAssetIdToScrollHandler);
  React.useEffect(() => { preview(getEmptyImage(), { captureDraggingState: true }); }, [preview]);

  const { container } = useClickOutside<HTMLDivElement>(Priority.SELECTION, {
    active: isSelected,
    onClickOutside: () => dispatch(removeSelectedAssetPanelComponent(componentId)),
  });

  const lexicalText = useMemo(() => {
    const updatedComponent = confirmLexicalStateInTextComponent(component, null);
    const lexicalState = updatedComponent.get('lexicalState', null);
    if (!lexicalState) {
      return '';
    }
    const editor = createLexicalTextEditor(lexicalState);

    return editor.read(() => $getRoot().getTextContent());
  }, [component]);

  const openPreview = (): void => {
    batch(() => {
      dispatch(setSelectedTextComponent(component));
      dispatch(showModal(ModalType.TEXT_COMPONENT_DETAILS));
    });
  };

  const deleteTextComponent = (): void => {
    const id = component.get('id');
    const entityType = component.get('entityType');
    dispatch(deleteComponent(id, entityType));
  };

  const onSelectHandler = (): void => {
    dispatch(addSelectedAssetPanelComponent(componentId));
  };

  return (
    <div
      className={classNames(styles.TextComponentItemWrapper, {
        [styles.TextComponentItemWrapper__bordered]: isDragging,
        [styles.TextComponentItemWrapper__active]: isSelected && allowDelete,
      })}
      ref={elementRef}
      onMouseDown={onSelectHandler}
      data-testid={dataTestId}
    >
      {dragRef(
        <div
          className={classNames(styles.TextComponentItem, {
            [styles.TextComponentItem__hidden]: isDragging,
          })}
          ref={container}
          data-testid={`${dataTestId}-item`}
        >
          <div className={styles.content} data-testid={`${dataTestId}-item-content`}>
            <div className={styles.iconWrapper} data-testid={`${dataTestId}-item-content-icon`}>
              <Icon type={IconType.TEXT} size="sm-md" color="primary" dataTestId={`${dataTestId}-item-content`} />
              {_.isFinite(textCount) && allowDelete && <div className={styles.counter} data-testid={`${dataTestId}-item-content-counter`}>
                {textCount}
              </div>}
            </div>
            <div className={styles.contentInfo} data-testid={`${dataTestId}-item-content-info`}>
              <div className={styles.contentInfoPrimary} data-testid={`${dataTestId}-content-citation-info-primary`}>
                {/* TODO: maybe create component, e.g. <TrimmedText text={text} rawContent={rawContent} /> for text output */}
                <span
                  dangerouslySetInnerHTML={{ __html: lexicalText }}
                  data-testid={`${dataTestId}-content-citation-info-primary-initialText`}
                />
                <span
                  className={styles.contentInfoPrimaryTruncation}
                  dangerouslySetInnerHTML={{ __html: trimText(lexicalText) }}
                  data-testid={`${dataTestId}-content-citation-info-primary-endText`}
                />
              </div>
            </div>
          </div>
          <AssetActions
            onDelete={allowDelete ? deleteTextComponent : undefined}
            onPreviewOpen={openPreview}
            dataTestId={`${dataTestId}-item-assetActions`}
          />
        </div>,
      )}
    </div>
  );
};

export default TextComponentItem;
